import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, Trash2, Eye, Code2 } from "lucide-react";
import { Markdown } from "../Markdown";
import {
  adminListNotes, adminCreateNote, adminUpdateNote, adminDeleteNote, adminImportNote,
} from "../../lib/api";

const EMPTY_NOTE = { title: "", content: "", tags: "", status: "draft" };

export function NotesTab({ onMutate }) {
  const [notes, setNotes] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(EMPTY_NOTE);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileInputRef = useRef(null);

  const load = () =>
    adminListNotes().then(setNotes).catch(() => toast.error("Could not load notes"));

  useEffect(() => { load(); }, []);

  const selectNote = (note) => {
    setSelectedId(note.id);
    setForm({ title: note.title, content: note.content, tags: (note.tags || []).join(", "), status: note.status });
    setPreview(false);
    setConfirmDelete(false);
  };

  const newNote = () => {
    setSelectedId(null);
    setForm(EMPTY_NOTE);
    setPreview(false);
    setConfirmDelete(false);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      content: form.content,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean).slice(0, 12),
      status: form.status,
    };
    try {
      if (selectedId) {
        await adminUpdateNote(selectedId, payload);
        toast.success("Note saved");
      } else {
        const created = await adminCreateNote(payload);
        setSelectedId(created.id);
        toast.success("Note created as draft");
      }
      await load();
      onMutate?.();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selectedId) return;
    try {
      await adminDeleteNote(selectedId);
      toast.success("Note deleted");
      newNote();
      await load();
      onMutate?.();
    } catch {
      toast.error("Delete failed");
    }
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const created = await adminImportNote(file);
      toast.success(`Imported "${created.title}" as a draft note`);
      await load();
      selectNote(created);
      onMutate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    }
  };

  const inputCls = "w-full px-3 py-2 rounded bg-deep border border-white/10 text-white text-xs font-mono focus:outline-none focus:border-cyber";

  return (
    <div data-testid="admin-notes-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <p className="font-mono text-xs text-dim">Notes are private in V1 — a public Notes page can come later.</p>
        <div className="flex items-center gap-2">
          <input ref={fileInputRef} type="file" accept=".md,.markdown,.txt" className="hidden" onChange={importFile} data-testid="note-import-input" />
          <button type="button" onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 rounded border border-white/20 text-dim font-mono text-xs hover:text-cyber hover:border-cyber transition-colors"
            data-testid="note-import-button">
            <Upload className="w-3.5 h-3.5" /> Import .md
          </button>
          <button type="button" onClick={newNote}
            className="flex items-center gap-2 px-4 py-2 rounded bg-cyber text-[#0b0f19] font-bold text-xs font-mono hover:bg-white transition-colors"
            data-testid="note-new-button">
            <Plus className="w-3.5 h-3.5" /> New Note
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Note list */}
        <div className="lg:col-span-4 space-y-2" data-testid="admin-notes-list">
          {notes === null ? (
            <p className="font-mono text-xs text-dim">loading ...</p>
          ) : notes.length === 0 ? (
            <p className="font-mono text-xs text-dim p-4 rounded bg-panelalt border border-white/5" data-testid="admin-notes-empty">
              No notes yet. Write one or import a Markdown file.
            </p>
          ) : (
            notes.map((n) => (
              <button key={n.id} type="button" onClick={() => selectNote(n)}
                className={`w-full text-left p-3 rounded border transition-colors ${
                  selectedId === n.id ? "bg-panel border-cyber" : "bg-panelalt border-white/5 hover:border-white/20"
                }`}
                data-testid={`note-item-${n.id}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-mono text-xs text-white font-bold truncate">{n.title}</span>
                  <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded font-bold shrink-0 ${
                    n.status === "published" ? "bg-emerald-500/20 text-emerald-400" : "bg-white/10 text-dim"
                  }`}>
                    {n.status.toUpperCase()}
                  </span>
                </div>
                <span className="font-mono text-[10px] text-dim">{(n.tags || []).join(" • ") || "no tags"}</span>
              </button>
            ))
          )}
        </div>

        {/* Editor */}
        <div className="lg:col-span-8 p-4 rounded bg-panelalt border border-white/5" data-testid="note-editor">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <input className={inputCls} placeholder="Note title *" value={form.title} maxLength={160}
              onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="note-form-title" />
            <input className={inputCls} placeholder="tags, comma separated" value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="note-form-tags" />
          </div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setPreview(false)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded font-mono text-[11px] transition-colors ${!preview ? "bg-cyber/20 text-cyber" : "text-dim hover:text-white"}`}
                data-testid="note-write-toggle">
                <Code2 className="w-3 h-3" /> Write
              </button>
              <button type="button" onClick={() => setPreview(true)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded font-mono text-[11px] transition-colors ${preview ? "bg-cyber/20 text-cyber" : "text-dim hover:text-white"}`}
                data-testid="note-preview-toggle">
                <Eye className="w-3 h-3" /> Preview
              </button>
            </div>
            <label className="flex items-center gap-2 font-mono text-[11px] text-dim cursor-pointer">
              <input type="checkbox" checked={form.status === "published"}
                onChange={(e) => setForm({ ...form, status: e.target.checked ? "published" : "draft" })}
                className="accent-[#38bdf8]" data-testid="note-form-published" />
              published
            </label>
          </div>
          {preview ? (
            <div className="min-h-[240px] p-4 rounded bg-deep border border-white/10">
              <Markdown content={form.content} />
            </div>
          ) : (
            <textarea rows={12} className={`${inputCls} resize-y`} placeholder="# My learning note&#10;&#10;Markdown is supported."
              value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="note-form-content" />
          )}
          <div className="flex items-center justify-between mt-3">
            {selectedId ? (
              confirmDelete ? (
                <button type="button" onClick={remove}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-500/20 text-red-400 font-mono text-[11px] font-bold hover:bg-red-500/30 transition-colors"
                  data-testid="note-confirm-delete">
                  <Trash2 className="w-3 h-3" /> Confirm delete?
                </button>
              ) : (
                <button type="button" onClick={() => setConfirmDelete(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-white/5 text-dim font-mono text-[11px] hover:text-red-400 transition-colors"
                  data-testid="note-delete-button">
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              )
            ) : <span />}
            <button type="button" onClick={save} disabled={saving}
              className="px-5 py-2 rounded bg-cyber text-[#0b0f19] font-bold font-mono text-xs hover:bg-white transition-colors disabled:opacity-50"
              data-testid="note-save-button">
              {saving ? "Saving..." : selectedId ? "Save Note" : "Create Note"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
